import Table from '@components/containers/dashboard/Table';
import LoadingTable from '@components/containers/dashboard/LoadingTable';
import Link from 'next/link';
import { useEffect, useMemo, useState, useRef } from 'react';
import { getOpenPositions } from '@components/eth/PositionInfo';
import { useContracts, useUserAddress, useIsClosePositionModal, useIsWrongNetwork } from '@components/store/getters';
import { userStore } from '@components/store';
import MobileModal from '@components/common/modals/MobileModal';
import { Contracts, Position } from 'types';
import Modal from '@components/common/Modal';
import Button from '@components/common/Button';
import { Wrapper, PageTitle, NavWrapper } from '@components/containers/dashboard/Dashboard.styles';
import { useAccount } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import Container from '@components/common/Container';
import { RefreshButton } from '@components/common/RefreshButton';
import { SortButton } from '@components/common/SortButton';
import { useModal } from 'hooks/useModal';
import { StyledButton } from './dashboard.styles';

export default function Dashboard() {
  const [isLoaded, setIsLoaded] = useState(false);
  const { isConnected } = useAccount();
  useEffect(() => {
    setIsLoaded(true);
  }, []);
  return (
    <Container>
      <Wrapper>
        <Container>
          <PageTitle type="h2">My Dashboard</PageTitle>
          {isLoaded && isConnected && <PositionsSection />}
          {isLoaded && !isConnected && <NotLoggedView />}
        </Container>
      </Wrapper>
    </Container>
  );
}

function NotLoggedView() {
  const { openConnectModal } = useConnectModal();
  return (
    <section>
      <p>&nbsp;</p>
      <p>Switch to desktop view to view your dashboard</p>
      <p>&nbsp;</p>
      <StyledButton onClick={openConnectModal}>Connect Wallet</StyledButton>
    </section>
  );
}

function PositionsSection() {
  const [openPositions, setOpenPositions] = useState([]);
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(false);
  const contracts = useContracts();
  const closePositionModalOpened = useIsClosePositionModal();
  const wrongNetworkModalOpened = useIsWrongNetwork();
  const { isConnected, address } = useAccount();
  const { isModalOpen, modalTitle, modalComponent, titleIconName, showLoader, closeModal, hideCloseButton } =
    useModal();

  const loadPositionsFromChain = async () => {
    // console.log('called loadPositionsFromChain');
    try {
      if (isConnected && !wrongNetworkModalOpened && contracts?.positionToken) {
        setLoading(true);
        const positions = await getOpenPositions(contracts, address);
        setOpenPositions(positions);
        userStore.setState({ positions: positions });
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      console.log('loadPositionsFromChain error: ', err);
    }
  };

  // We can use the closePositionModalOpened store to refresh the data
  useEffect(() => {
    if (contracts?.positionToken) loadPositionsFromChain();
  }, [contracts, closePositionModalOpened]);

  const sortHandler = () => {
    // For now just toggle text between newest and oldest
    // TODO sort the actual data
    const newSort = sort === 'newest' ? 'oldest' : 'newest';
    setSort(newSort);
  };

  // We want a minimum time needed to load the data
  // Otherwise the animation is too jumpy
  // Not too long though, so 1 seconds (1000ms)
  const timeoutRef = useRef(null);
  const refreshHandler = () => {
    setLoading(true);
    if (window && window.setTimeout) {
      if (timeoutRef.current) clearInterval(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        loadPositionsFromChain();
      }, 1000);
    }
  };

  const sortedPositions = useMemo(() => {
    if (!openPositions?.length) return [];
    if (sort === 'newest') return [...openPositions].reverse();
    return openPositions;
  }, [openPositions, sort]);

  function renderNav() {
    return (
      <NavWrapper>
        <section>
          <h4>
            Open Positions
            <span onClick={refreshHandler}>
              <RefreshButton loading={loading.toString()} />
            </span>
          </h4>
          <span onClick={sortHandler}>
            Sort by:
            <strong>{sort}</strong>
            <SortButton sort={sort} />
          </span>
          <span>{openPositions.length} Open Positions</span>
        </section>
        <Link href="/open-position">
          <Button>Open New</Button>
        </Link>
      </NavWrapper>
    );
  }

  return (
    <section>
      {renderNav()}
      {!loading && <Table openPositions={sortedPositions} />}
      {loading && <LoadingTable />}
      {isModalOpen && (
        <Modal
          title={modalTitle}
          onClose={closeModal}
          withLoader={showLoader}
          hideCloseButton={hideCloseButton}
          titleIcon={titleIconName}
        >
          {modalComponent}
        </Modal>
      )}
      <MobileModal />
    </section>
  );
}
